import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { classroomApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Send, Users, BookOpen, BarChart3, MessageSquare, Plus, Calendar, ClipboardCopy, FileText, Clock, Check, Trash2, ChevronDown } from 'lucide-react';

// Stream Tab
const StreamTab = ({ classId, isTeacher, user }) => {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [commentText, setCommentText] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    classroomApi.getStream(classId).then(d => { setPosts(d.posts || []); setLoading(false); }).catch(() => setLoading(false));
  }, [classId]);

  const handlePost = async () => {
    if (!newPost.trim()) return;
    try {
      const post = await classroomApi.postToStream(classId, newPost);
      setPosts(prev => [post, ...prev]);
      setNewPost('');
    } catch { toast.error('Failed to post'); }
  };

  const handleComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;
    try {
      const comment = await classroomApi.addComment(postId, text);
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, comments: [...(p.comments || []), comment] } : p));
      setCommentText(prev => ({ ...prev, [postId]: '' }));
    } catch { toast.error('Failed to comment'); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="stream-tab">
      <div className="rounded-xl border border-border bg-card p-4">
        <Textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder="Share something with your class..." className="mb-3 min-h-[80px]" data-testid="stream-post-input" />
        <div className="flex justify-end">
          <Button onClick={handlePost} disabled={!newPost.trim()} data-testid="stream-post-btn">
            <Send className="w-4 h-4 mr-1" /> Post
          </Button>
        </div>
      </div>

      {posts.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No posts yet. Start the conversation!</p>
      ) : posts.map(post => (
        <div key={post.post_id} className="rounded-xl border border-border bg-card p-4" data-testid={`stream-post-${post.post_id}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
              {post.author_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{post.author_name}</p>
              <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleString()}</p>
            </div>
            {post.author_role === 'faculty' && <Badge variant="secondary" className="text-[10px]">Teacher</Badge>}
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap">{post.content}</p>
          {post.assignment_id && (
            <Link to={`/classroom/${classId}/assignment/${post.assignment_id}`} className="inline-block mt-2">
              <Badge className="cursor-pointer hover:bg-primary/90">View Assignment</Badge>
            </Link>
          )}

          {/* Comments */}
          {(post.comments?.length > 0 || true) && (
            <div className="mt-3 pt-3 border-t border-border">
              {post.comments?.length > 0 && (
                <button onClick={() => setExpandedComments(p => ({...p, [post.post_id]: !p[post.post_id]}))}
                  className="text-xs text-muted-foreground hover:text-foreground mb-2 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> {post.comments.length} comment{post.comments.length !== 1 ? 's' : ''}
                  <ChevronDown className={`w-3 h-3 transition-transform ${expandedComments[post.post_id] ? 'rotate-180' : ''}`} />
                </button>
              )}
              {expandedComments[post.post_id] && post.comments?.map(c => (
                <div key={c.comment_id} className="flex gap-2 py-1.5">
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold shrink-0">
                    {c.author_name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-foreground">{c.author_name}</span>
                    <p className="text-xs text-foreground/80">{c.content}</p>
                  </div>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <Input value={commentText[post.post_id] || ''} onChange={e => setCommentText(p => ({...p, [post.post_id]: e.target.value}))}
                  placeholder="Add a comment..." className="text-sm h-8" onKeyDown={e => e.key === 'Enter' && handleComment(post.post_id)} />
                <Button size="sm" variant="ghost" onClick={() => handleComment(post.post_id)} className="h-8 px-2">
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Classwork Tab
const ClassworkTab = ({ classId, isTeacher }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', instructions: '', type: 'assignment', points: 100, due_date: '' });

  useEffect(() => {
    classroomApi.getAssignments(classId).then(d => { setAssignments(d.assignments || []); setLoading(false); }).catch(() => setLoading(false));
  }, [classId]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      const a = await classroomApi.createAssignment({ ...form, class_id: classId });
      setAssignments(prev => [a, ...prev]);
      setShowCreate(false);
      setForm({ title: '', description: '', instructions: '', type: 'assignment', points: 100, due_date: '' });
      toast.success('Assignment created!');
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />)}</div>;

  return (
    <div data-testid="classwork-tab">
      {isTeacher && (
        <Button onClick={() => setShowCreate(true)} className="mb-4" data-testid="create-assignment-btn">
          <Plus className="w-4 h-4 mr-1" /> Create Assignment
        </Button>
      )}

      {assignments.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No assignments yet.</p>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => (
            <Link key={a.assignment_id} to={`/classroom/${classId}/assignment/${a.assignment_id}`}
              className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:bg-muted/50 transition-colors" data-testid={`assignment-${a.assignment_id}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${a.type === 'quiz' ? 'bg-amber-500/10 text-amber-600' : 'bg-primary/10 text-primary'}`}>
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm text-foreground truncate">{a.title}</h3>
                  <Badge variant="outline" className="text-[10px] shrink-0">{a.type}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  {a.due_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Due: {new Date(a.due_date).toLocaleDateString()}</span>}
                  <span>{a.points} pts</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                {isTeacher ? (
                  <span className="text-xs text-muted-foreground">{a.submission_count || 0} submitted / {a.graded_count || 0} graded</span>
                ) : a.my_submission ? (
                  <Badge variant={a.my_submission.status === 'graded' ? 'default' : 'secondary'} className={a.my_submission.status === 'graded' ? 'bg-emerald-600' : ''}>
                    {a.my_submission.status === 'graded' ? `${a.my_submission.grade}/${a.points}` : 'Submitted'}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">Not submitted</Badge>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create Assignment Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-assignment-dialog">
          <DialogHeader><DialogTitle>Create Assignment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-foreground">Title *</label>
              <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Assignment title" data-testid="assignment-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Instructions</label>
              <Textarea value={form.instructions} onChange={e => setForm(p => ({...p, instructions: e.target.value}))} placeholder="Instructions for students..." rows={3} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground">Type</label>
                <select value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="assignment">Assignment</option>
                  <option value="quiz">Quiz</option>
                  <option value="material">Material</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Points</label>
                <Input type="number" value={form.points} onChange={e => setForm(p => ({...p, points: parseInt(e.target.value) || 0}))} />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Due Date</label>
                <Input type="date" value={form.due_date} onChange={e => setForm(p => ({...p, due_date: e.target.value}))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-assignment-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// People Tab
const PeopleTab = ({ classData }) => {
  const students = classData?.student_details || [];
  return (
    <div data-testid="people-tab">
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">Teacher</h3>
        <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
            {classData?.teacher_name?.[0]?.toUpperCase() || 'T'}
          </div>
          <span className="font-medium text-foreground">{classData?.teacher_name}</span>
          <Badge variant="secondary" className="text-[10px]">Teacher</Badge>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" /> Students ({students.length})
        </h3>
        {students.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No students enrolled yet. Share the class code to invite students.</p>
        ) : (
          <div className="space-y-1">
            {students.map(s => (
              <div key={s.user_id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-bold">
                  {s.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.email}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Grades Tab
const GradesTab = ({ classId, isTeacher }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    classroomApi.getGrades(classId).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [classId]);

  if (loading) return <div className="h-40 bg-muted animate-pulse rounded-xl" />;

  if (isTeacher && data?.grade_data) {
    const assignments = data.assignments || [];
    return (
      <div className="overflow-x-auto" data-testid="grades-tab">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-2 font-semibold text-foreground sticky left-0 bg-background">Student</th>
              {assignments.map(a => (
                <th key={a.assignment_id} className="text-center p-2 font-medium text-foreground min-w-[100px]">
                  <span className="text-xs">{a.title}</span>
                  <br /><span className="text-[10px] text-muted-foreground">/{a.points}</span>
                </th>
              ))}
              <th className="text-center p-2 font-semibold text-foreground">Average</th>
            </tr>
          </thead>
          <tbody>
            {data.grade_data.map(row => {
              const grades = assignments.map(a => row.grades[a.assignment_id]);
              const scored = grades.filter(g => g?.grade !== null && g?.grade !== undefined);
              const avg = scored.length > 0 ? Math.round(scored.reduce((s, g) => s + g.grade, 0) / scored.length) : '-';
              return (
                <tr key={row.student_id} className="border-b border-border/50 hover:bg-muted/30">
                  <td className="p-2 font-medium text-foreground sticky left-0 bg-background">{row.student_name}</td>
                  {assignments.map(a => {
                    const sub = row.grades[a.assignment_id];
                    return (
                      <td key={a.assignment_id} className="text-center p-2">
                        {sub?.grade !== null && sub?.grade !== undefined ? (
                          <span className={`font-mono text-sm ${sub.grade >= a.points * 0.7 ? 'text-emerald-600' : sub.grade >= a.points * 0.4 ? 'text-amber-600' : 'text-red-600'}`}>
                            {sub.grade}
                          </span>
                        ) : sub?.status === 'submitted' ? (
                          <Badge variant="outline" className="text-[10px]">Pending</Badge>
                        ) : (
                          <span className="text-muted-foreground/40">-</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="text-center p-2 font-semibold text-foreground">{avg}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // Student view
  if (data?.my_grades) {
    return (
      <div className="space-y-2" data-testid="grades-tab">
        {data.my_grades.map(({ assignment: a, submission: sub }) => (
          <div key={a.assignment_id} className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
            <div>
              <h4 className="font-semibold text-sm text-foreground">{a.title}</h4>
              <p className="text-xs text-muted-foreground">{a.type} - {a.points} pts</p>
            </div>
            <div className="text-right">
              {sub?.grade !== null && sub?.grade !== undefined ? (
                <div>
                  <span className="text-lg font-bold text-foreground">{sub.grade}</span>
                  <span className="text-muted-foreground">/{a.points}</span>
                  {sub.feedback && <p className="text-xs text-muted-foreground mt-1">{sub.feedback}</p>}
                </div>
              ) : sub ? (
                <Badge variant="secondary">Submitted</Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600">Not submitted</Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <p className="text-center text-muted-foreground">No grade data available.</p>;
};

// Main ClassDetail component
export default function ClassDetail() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('stream');
  const isTeacher = classData?.is_teacher || ['super_admin', 'org_admin'].includes(user?.role);

  useEffect(() => {
    classroomApi.getClass(classId).then(d => { setClassData(d); setLoading(false); }).catch(e => { toast.error(e.message); setLoading(false); });
  }, [classId]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this class and all its data?')) return;
    try {
      await classroomApi.deleteClass(classId);
      toast.success('Class deleted');
      navigate('/classroom');
    } catch (e) { toast.error(e.message); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(classData?.class_code || '');
    toast.success('Class code copied!');
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!classData) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Class not found</div>;

  const TABS = [
    { id: 'stream', label: 'Stream', icon: MessageSquare },
    { id: 'classwork', label: 'Classwork', icon: BookOpen },
    { id: 'people', label: 'People', icon: Users },
    { id: 'grades', label: 'Grades', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="class-detail">
      {/* Header Banner */}
      <div className="h-32 relative" style={{ backgroundColor: classData.color || '#6366f1' }}>
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-5xl mx-auto px-4 h-full flex items-end pb-4">
          <div className="flex items-end justify-between w-full">
            <div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/classroom')} className="text-white/80 hover:text-white hover:bg-white/10 mb-2">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <h1 className="text-2xl font-bold text-white">{classData.name}</h1>
              <p className="text-white/80 text-sm">{classData.section && `${classData.section} · `}{classData.subject}</p>
            </div>
            {isTeacher && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={copyCode} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="copy-code-btn">
                  <ClipboardCopy className="w-4 h-4 mr-1" /> {classData.class_code}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleDelete} className="text-white/60 hover:text-red-300 hover:bg-white/10" data-testid="delete-class-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border bg-background sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 flex gap-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {tab === 'stream' && <StreamTab classId={classId} isTeacher={isTeacher} user={user} />}
        {tab === 'classwork' && <ClassworkTab classId={classId} isTeacher={isTeacher} />}
        {tab === 'people' && <PeopleTab classData={classData} />}
        {tab === 'grades' && <GradesTab classId={classId} isTeacher={isTeacher} />}
      </div>
    </div>
  );
}
